import { formatDate } from './dateUtils';

const TRACKER_NOTE_MARKER = /^Imported from Tracker\.?\s*/i;
const IMPORTED_NOTE_MARKERS = [
  TRACKER_NOTE_MARKER,
  /CROSS-DEPT/i,
  /Cross-dept rescue/i,
  /Origin Reject row unchanged/i,
  /trust_flag:/i,
];
const SYSTEM_NOISE = [
  /^Origin reject row unchanged\.?$/i,
  /^Do not auto-email managers\.?$/i,
  /^trust_flag:\s*\S+$/i,
  /^(Reject|Pass|Borderline)\([^)]+\)\s*[→\-–]+\s*(Pass|Reject|Borderline)\b/i,
  /^filed\s+\d{4}-\d{2}-\d{2}\.?$/i,
];
const YEAR_RANGE =
  /^(?:\d{4}\s*[–-]\s*(?:\d{4}|[A-Za-z]{3,9}\s+\d{4})(?:\s*&\s*.+)?)$/;
const SCORE_WORDS = {
  pass: 'passed',
  hire: 'passed',
  reject: 'rejected',
  borderline: 'kept in view',
  kiv: 'kept in view',
};
const CROSS_DEPT_PATTERN =
  /^Cross-dept rescue(?:\s+(\d{4}-\d{2}-\d{2}))?:\s*origin\s+(\S+)\s+on\s+(.+?)\s*(?:->|→)\s*(.+)$/i;
const CROSS_DEPT_SHORT_PATTERN = /^Cross-dept rescue(?:\s+to\s+(.+))?$/i;
const ALSO_CONSIDERED_PATTERN =
  /^Also considered for (.+?)\s+[—-]\s+(Pass|Reject|Borderline)\s*\(\s*CROSS-DEPT\s*\)\.\s*Origin:\s*(.+?)\s+(Reject|Pass)\b/i;
const DESTINATION_PATTERN = /^(Pass|Borderline|Reject|Hire|KIV)\s+(.+)$/i;
const HEADING_RULES = [
  { pattern: /^why\s+not\s+reject\s*:/i, key: 'rationale', label: 'Rationale' },
  { pattern: /^why\s+pass\b[^:\n]*:/i, key: 'rationale', label: 'Rationale' },
  { pattern: /^why\s*:/i, key: 'rationale', label: 'Rationale' },
  { pattern: /^job hopping\s*:/i, key: 'concerns', label: 'Concerns' },
  { pattern: /^concerns?\s*:/i, key: 'concerns', label: 'Concerns' },
  { pattern: /^gaps?\s*(?:\([^)]*\))?\s*:/i, key: 'concerns', label: 'Concerns' },
  { pattern: /^reed preference-fit[^:\n]*:/i, key: 'preference_fit', label: 'Hiring preference' },
  { pattern: /^strengths?\s*:/i, key: 'strengths', label: 'Strengths' },
  { pattern: /^decision\s*:/i, key: 'decision', label: 'Decision context' },
  { pattern: /^process\s*:/i, key: 'process', label: 'Process' },
  { pattern: /^next action\s*:/i, key: 'next_action', label: 'Next action' },
  { pattern: /^jd source update\b[^:\n]*:/i, key: 'jd_update', label: 'JD check' },
];
const SECTION_ORDER = [
  'transfer',
  'assessment',
  'rationale',
  'strengths',
  'concerns',
  'preference_fit',
  'decision',
  'process',
  'next_action',
];

const stripImporterTrack = (role) =>
  String(role || '')
    .replace(/\s*\(([^)]*-[^)]*)\)\s*$/g, '')
    .trim();

const humanizeScore = (score) => SCORE_WORDS[String(score || '').toLowerCase()] || '';

const headingRuleFor = (line) =>
  HEADING_RULES.find(({ pattern }) => pattern.test(String(line || '').trim()));

const isEchoLine = (line) => {
  const text = String(line || '').trim();
  return (
    /^scored\s+\d{4}-\d{2}-\d{2}\b/i.test(text) ||
    /^(screening\s+)?pass\s+[—-]\s+hiring confirms pass/i.test(text) ||
    /^screening pass\b/i.test(text) ||
    /^[-*]\s*card:\s*\S+/i.test(text) ||
    /^[-*]\s*locked from designation\/jd sheet/i.test(text)
  );
};

const cleanImporterChrome = (text) =>
  String(text || '')
    .replace(/\([^)]*sheet\s+[A-Za-z0-9_-]{12,}[^)]*\)/gi, '')
    .replace(/\bMCF-\d{4}-\d+\b/g, '')
    .replace(/\/workspace\/\S+/g, '')
    .replace(/\btrust_flag:\s*\S+/gi, '')
    .replace(/\s*Origin Reject row unchanged\.?/gi, '')
    .replace(/\s*Do not auto-email managers\.?/gi, '')
    .replace(/^filed\s+\d{4}-\d{2}-\d{2}\.\s*/i, '')
    .replace(/\(\s*\)/g, '')
    .replace(/[ \t]+$/gm, '')
    .replace(/[ ]{2,}/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

const stripEchoLines = (text) =>
  String(text || '')
    .split('\n')
    .filter((line) => line.trim() && !isEchoLine(line))
    .join('\n')
    .trim();

const peelTagHr = (text) => {
  const match = String(text || '').match(
    /Tag HR verify\s*[—-]\s*([^.|]+)(?:\.|$)/i
  );
  if (!match) {
    return { text, concern: '' };
  }

  return {
    text: text.replace(/\s*Tag HR verify[\s\S]*$/i, '').trim(),
    concern: match[1].trim(),
  };
};

const splitDecisionPipes = (text) => {
  const parts = String(text || '').split(/\s+\|\s+/);
  const kept = [];

  for (let index = 0; index < parts.length; index += 1) {
    const next = (parts[index + 1] || '').trim();
    if (YEAR_RANGE.test(next)) {
      index += 2;
      continue;
    }
    kept.push(parts[index].trim());
  }

  return kept.filter(Boolean);
};

const humanizeTransfer = (text) => {
  const alsoConsidered = text.match(ALSO_CONSIDERED_PATTERN);
  if (alsoConsidered) {
    const [, destinationRole, destinationScore, originRole, originScore] =
      alsoConsidered;
    const originVerb = humanizeScore(originScore) || originScore.toLowerCase();
    const destinationVerb =
      humanizeScore(destinationScore) || destinationScore.toLowerCase();

    return {
      key: 'transfer',
      label: 'How they arrived',
      text: `Originally ${originVerb} for ${originRole.trim()}. Later ${destinationVerb} for ${destinationRole.trim()}.`,
    };
  }

  const detailed = text.match(CROSS_DEPT_PATTERN);
  if (detailed) {
    const [, rawDate, originScore, originRoleRaw, destinationRaw] = detailed;
    const destination = destinationRaw.trim();
    const destinationMatch = destination.match(DESTINATION_PATTERN);
    const destinationScore = destinationMatch?.[1];
    const destinationRole = stripImporterTrack(
      destinationMatch?.[2] || destination
    );
    const originRole = stripImporterTrack(originRoleRaw);
    const originVerb = humanizeScore(originScore) || originScore.toLowerCase();
    const destinationVerb = humanizeScore(destinationScore);
    const dateLabel = rawDate ? formatDate(`${rawDate}T00:00:00+08:00`) : '';
    const destinationClause = destinationVerb
      ? `Later ${destinationVerb} for ${destinationRole}`
      : `Later considered for ${destinationRole}`;
    const dateClause = dateLabel ? ` on ${dateLabel}` : '';

    return {
      key: 'transfer',
      label: 'How they arrived',
      text: `Originally ${originVerb} for ${originRole}. ${destinationClause}${dateClause}.`,
    };
  }

  const shortened = text.match(CROSS_DEPT_SHORT_PATTERN);
  if (!shortened) return null;

  const destination = String(shortened[1] || '').trim();
  return {
    key: 'transfer',
    label: 'How they arrived',
    text: destination
      ? `Moved to ${destination} after the original role was not a fit.`
      : 'Moved from another role after the original application was not a fit.',
  };
};

const extractJdGaps = (text) => {
  const gapMatch = text.match(/gaps?\s*(?:\([^)]*\))?\s*:\s*([\s\S]+)/i);
  if (!gapMatch) return [];

  return [
    {
      key: 'concerns',
      label: 'Concerns',
      text: gapMatch[1].replace(/^[-*]\s*/gm, '').trim(),
    },
  ];
};

const splitByHeadings = (block) => {
  const lines = String(block || '').split('\n');
  const chunks = [];
  let current = [];

  lines.forEach((line) => {
    if (headingRuleFor(line) && current.length) {
      chunks.push(current.join('\n').trim());
      current = [line];
      return;
    }
    current.push(line);
  });

  if (current.length) {
    chunks.push(current.join('\n').trim());
  }

  return chunks.filter(Boolean);
};

const classifyChunk = (rawText) => {
  const text = cleanImporterChrome(stripEchoLines(rawText));
  const sections = [];

  if (!text || SYSTEM_NOISE.some((pattern) => pattern.test(text))) {
    return sections;
  }

  if (/^jd source update\b/i.test(text)) {
    return [...sections, ...extractJdGaps(text)];
  }

  const transfer = humanizeTransfer(text);
  if (transfer) return [...sections, transfer];

  const heading = headingRuleFor(text);
  if (heading) {
    sections.push({
      key: heading.key,
      label: heading.label,
      text: text.replace(heading.pattern, '').trim(),
    });
    return sections;
  }

  const inlineWhy = text.match(/\bWHY\s+Pass\b[^:]*:\s*([\s\S]+)/i);
  if (inlineWhy) {
    sections.push({
      key: 'rationale',
      label: 'Rationale',
      text: inlineWhy[1].trim(),
    });
    return sections;
  }

  sections.push({
    key: 'assessment',
    label: 'Assessment',
    text,
  });
  return sections;
};

const mergeSections = (sections) => {
  const merged = [];
  const indexByKey = new Map();

  sections.forEach((section) => {
    if (!section?.text) return;
    const existingIndex = indexByKey.get(section.key);
    if (existingIndex == null) {
      indexByKey.set(section.key, merged.length);
      merged.push({ ...section });
      return;
    }
    merged[existingIndex] = {
      ...merged[existingIndex],
      text: `${merged[existingIndex].text}\n\n${section.text}`,
    };
  });

  return merged.sort(
    (left, right) =>
      SECTION_ORDER.indexOf(left.key) - SECTION_ORDER.indexOf(right.key)
  );
};

export const isTrackerImportedReview = (review) => {
  if (review?.source) {
    return review.source === 'applicant_tracker';
  }

  const notes = String(review?.notes || '');
  return IMPORTED_NOTE_MARKERS.some((pattern) => pattern.test(notes));
};

export const reviewNoteSections = (notes) => {
  const readableNotes = String(notes || '')
    .replace(TRACKER_NOTE_MARKER, '')
    .trim();

  if (!readableNotes) return [];

  const peeled = peelTagHr(readableNotes);
  const sections = peeled.text
    .split(/\n{2,}/)
    .flatMap(splitDecisionPipes)
    .flatMap(splitByHeadings)
    .flatMap(classifyChunk);

  if (peeled.concern) {
    sections.push({
      key: 'concerns',
      label: 'Concerns',
      text: peeled.concern,
    });
  }

  return mergeSections(sections);
};
